/**
 * Inventory locations nest (a walk-in fridge holds shelves, a shelf holds
 * racks), but each one stores only its own name. On its own, "Rack 2" doesn't
 * say which rack — so wherever a location is shown, it's shown with the path
 * down to it: "Walk In Fridge › Shelf 1 › Rack 2".
 *
 * Shared by the API (which names locations in recall traces, box lists and
 * stocktake scans) and the web app, so both draw the same path.
 */

/** The fields a path is built from. `_id`/`parent` may be ObjectIds. */
export interface LocationPathNode {
  _id: unknown;
  name: string;
  parent?: unknown;
}

/** What separates the levels of a displayed path. */
export const LOCATION_PATH_SEPARATOR = ' › ';

/**
 * Every location's path, as the names from its top-level ancestor down to
 * itself. A parent that isn't in the list (deleted, or another vendor's) ends
 * the path there, and a parent cycle — which a bad edit could create — is cut
 * rather than looped forever.
 * @param locations - A vendor's locations
 * @returns Location id → names, outermost first
 */
export function buildLocationPaths(
  locations: readonly (LocationPathNode | null | undefined)[],
): Map<string, string[]> {
  const byId = new Map<string, LocationPathNode>();
  for (const l of locations) if (l) byId.set(String(l._id), l);

  const paths = new Map<string, string[]>();
  const pathOf = (id: string): string[] => {
    const cached = paths.get(id);
    if (cached) return cached;
    const chain: string[] = [];
    const seen = new Set<string>();
    let cur = byId.get(id);
    while (cur && !seen.has(String(cur._id))) {
      seen.add(String(cur._id));
      chain.unshift(cur.name);
      cur = cur.parent != null ? byId.get(String(cur.parent)) : undefined;
    }
    paths.set(id, chain);
    return chain;
  };
  for (const id of byId.keys()) pathOf(id);
  return paths;
}

/**
 * A path as one line of text.
 * @param parts - Names, outermost first
 * @returns e.g. "Walk In Fridge › Shelf 1 › Rack 2"
 */
export function formatLocationPath(parts: readonly string[]): string {
  return parts.join(LOCATION_PATH_SEPARATOR);
}

/**
 * Location id → its displayed path, for code that only needs the text.
 * @param locations - A vendor's locations
 * @returns Location id → "Walk In Fridge › Shelf 1 › Rack 2"
 */
export function buildLocationPathLabels(
  locations: readonly (LocationPathNode | null | undefined)[],
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const [id, parts] of buildLocationPaths(locations)) {
    labels.set(id, formatLocationPath(parts));
  }
  return labels;
}
